import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneInput } from "@/components/PhoneInput";
import { ArrowRight, ArrowLeft, Plus, Trash2, Check, Sparkles, CalendarHeart, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyEvent = () => ({ _key: Math.random().toString(36).slice(2), event_type: "", date: "", slots: [], location: "", ready_by: "" });

const inputCls = "w-full px-4 py-3.5 bg-white border border-[#EAE7E0] text-[#2A2421] focus:border-[#C88270] focus:outline-none text-base";
const labelCls = "block text-[11px] uppercase tracking-[0.22em] text-[#4A4540] font-medium mb-2";

export default function EnquiryFormPage() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [events, setEvents] = useState([emptyEvent()]);
  const [expandedEvent, setExpandedEvent] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/enquiry-config`).then((r) => setConfig(r.data)).catch(() => setConfig({ studio: { name: "Dejavu Makeup by Vinni", tagline: "" }, event_types: [], slots: ["Morning", "Afternoon", "Evening", "Night"] }));
  }, []);

  const studio = config?.studio || {};
  const eventTypes = config?.event_types || [];
  const slots = config?.slots || ["Morning", "Afternoon", "Evening", "Night"];
  const webBg = config?.bg_web ? `${API}/public/portfolio/${config.bg_web}?w=1600` : "/enquiry-bg-web.webp?v=2";
  const mobileBg = config?.bg_mobile ? `${API}/public/portfolio/${config.bg_mobile}?w=820` : "/enquiry-bg-mobile.webp?v=2";

  const setEvent = (key, patch) => setEvents((prev) => prev.map((e) => (e._key === key ? { ...e, ...patch } : e)));
  const toggleSlot = (key, slot) => setEvents((prev) => prev.map((e) => (e._key === key ? { ...e, slots: e.slots.includes(slot) ? e.slots.filter((s) => s !== slot) : [...e.slots, slot] } : e)));
  const addEvent = () => { setEvents((prev) => [...prev, emptyEvent()]); setExpandedEvent(events.length); };
  const delEvent = (key) => { setEvents((prev) => (prev.length > 1 ? prev.filter((e) => e._key !== key) : prev)); setExpandedEvent(0); };

  const next = () => {
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Please enter a valid phone number"); return; }
    setStep(2);
  };

  const submit = async () => {
    const valid = events.filter((e) => e.event_type);
    if (valid.length === 0) { toast.error("Please select at least one event"); return; }
    for (let i = 0; i < valid.length; i++) {
      const e = valid[i];
      if (!e.date) { setExpandedEvent(i); toast.error(`Event ${i + 1}: please pick a date`); return; }
      if (!e.slots.length) { setExpandedEvent(i); toast.error(`Event ${i + 1}: please select at least one slot`); return; }
      if (!e.location.trim()) { setExpandedEvent(i); toast.error(`Event ${i + 1}: please enter a location`); return; }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/public/enquiries`, {
        client_name: name.trim(), client_phone: phone.trim(),
        events: valid.map(({ _key, ...e }) => e),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSaving(false); }
  };

  if (!config) return <div className="min-h-screen bg-[#FAF9F6]" />;

  return (
    <div className="relative min-h-screen text-[#2A2421] flex flex-col" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="enquiry-page">
      <div className="fixed inset-0 z-0 bg-[#EFE7E0]" aria-hidden="true">
        <div className="absolute inset-0 hidden md:block" style={{ backgroundImage: `url('${webBg}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 md:hidden" style={{ backgroundImage: `url('${mobileBg}')`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/35" />
      </div>

      <main className="relative z-10 flex-1 w-full flex items-start md:items-center justify-center px-4 sm:px-6 pt-32 pb-12 sm:py-14">
        <div className="w-full max-w-xl bg-[#FAF9F6] border border-[#EAE7E0] shadow-2xl rounded-lg px-5 sm:px-10 py-7 sm:py-10">
          <div className="flex justify-center mb-7">
            <img src="/dejavu-logo.png?v=3" alt={studio.name} className="h-9 w-auto object-contain" />
          </div>
        {done ? (
          <div className="text-center py-10" data-testid="enquiry-thankyou">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#C88270] flex items-center justify-center mb-6"><Check size={30} className="text-white" /></div>
            <h1 className="font-serif text-4xl sm:text-5xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank you, {name.split(" ")[0]}!</h1>
            <p className="text-[#5C5652] text-base max-w-md mx-auto leading-relaxed">We'll quickly check Vinni's availability for your dates and arrange a look consultation call with you shortly. Talk soon!</p>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center gap-2.5 bg-[#C88270]/10 border border-[#C88270]/30 px-4 py-3" data-testid="availability-highlight">
              <CalendarHeart size={17} className="text-[#C88270] shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-[#9A5A48] tracking-wide">Complete the form to check Vinni's availability</span>
            </div>
            <div className="mb-8">
              <div className="flex items-center gap-2 text-[#9A5A48] mb-3"><Sparkles size={16} /><span className="text-[11px] uppercase tracking-[0.3em] font-semibold">Event Enquiry</span></div>
              <h1 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {step === 1 ? "Let's start with you" : "Tell us about your events"}
              </h1>
              {studio.tagline && <p className="text-[#5C5652] mt-2 text-sm">{studio.tagline}</p>}
              <div className="flex gap-1.5 mt-5">
                <span className={`h-1 flex-1 rounded-full ${step >= 1 ? "bg-[#C88270]" : "bg-[#EAE7E0]"}`} />
                <span className={`h-1 flex-1 rounded-full ${step >= 2 ? "bg-[#C88270]" : "bg-[#EAE7E0]"}`} />
              </div>
            </div>

            {step === 1 ? (
              <div className="space-y-6">
                <div>
                  <label className={labelCls}>Bride / Client name *</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="enq-name" placeholder="e.g. Priya Sharma" className={inputCls} autoFocus />
                </div>
                <div>
                  <label className={labelCls}>Phone (WhatsApp preferred) *</label>
                  <PhoneInput value={phone} onChange={setPhone} testId="enq-phone" large placeholder="98XXXXXXXX" />
                </div>
                <button onClick={next} data-testid="enq-next" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2A2421] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#C88270] transition-colors">
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {events.map((ev, i) => {
                  const open = expandedEvent === i;
                  const summary = [ev.event_type || `Event ${i + 1}`, ev.date || null].filter(Boolean).join(" · ");
                  return (
                  <div key={ev._key} className="bg-white border border-[#EAE7E0]" data-testid={`enq-event-${i}`}>
                    <div className="flex items-center gap-2 p-4">
                      <button type="button" onClick={() => setExpandedEvent(open ? -1 : i)} data-testid={`enq-event-toggle-${i}`} className="flex-1 min-w-0 flex items-center justify-between text-left gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <CalendarHeart size={16} className="text-[#C88270] shrink-0" />
                          <span className="text-sm font-medium text-[#2A2421] truncate">{summary}</span>
                        </span>
                        {open ? <ChevronUp size={18} className="text-[#5C5652] shrink-0" /> : <ChevronDown size={18} className="text-[#5C5652] shrink-0" />}
                      </button>
                      {events.length > 1 && <button onClick={() => delEvent(ev._key)} data-testid={`enq-del-${i}`} className="text-[#5C5652] hover:text-red-600 shrink-0"><Trash2 size={16} /></button>}
                    </div>
                    {open && (
                    <div className="px-5 pb-5 pt-4 space-y-4 border-t border-[#EAE7E0]">
                      <div>
                        <label className={labelCls}>Event type *</label>
                        <select value={ev.event_type} onChange={(e) => setEvent(ev._key, { event_type: e.target.value })} data-testid={`enq-type-${i}`} className={inputCls}>
                          <option value="">Select an event…</option>
                          {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Event date *</label>
                        <input type="date" value={ev.date} onChange={(e) => setEvent(ev._key, { date: e.target.value })} data-testid={`enq-date-${i}`} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Slot to book *</label>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((s) => (
                            <button key={s} onClick={() => toggleSlot(ev._key, s)} data-testid={`enq-slot-${i}-${s}`}
                              className={`px-4 py-2 text-sm border transition-colors ${ev.slots.includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div><label className={labelCls}>Location *</label><input value={ev.location} onChange={(e) => setEvent(ev._key, { location: e.target.value })} data-testid={`enq-loc-${i}`} placeholder="Venue / area" className={inputCls} /></div>
                        <div><label className={labelCls}>Ready by <span className="text-[#B8B2AA] normal-case tracking-normal">(optional)</span></label><input value={ev.ready_by} onChange={(e) => setEvent(ev._key, { ready_by: e.target.value })} data-testid={`enq-ready-${i}`} placeholder="e.g. 6:00 AM" className={inputCls} /></div>
                      </div>
                    </div>
                    )}
                  </div>
                  );
                })}
                <button onClick={addEvent} data-testid="enq-add-event" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#9A5A48] hover:text-[#2A2421]"><Plus size={15} /> Add another event</button>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button onClick={() => setStep(1)} data-testid="enq-back" className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-[#EAE7E0] text-[#5C5652] text-xs uppercase tracking-[0.2em] hover:border-[#2A2421]"><ArrowLeft size={16} /> Back</button>
                  <button onClick={submit} disabled={saving} data-testid="enq-submit" className="flex-1 inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#2A2421] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#C88270] transition-colors disabled:opacity-50">
                    {saving ? "Sending…" : <>Submit enquiry <Check size={16} /></>}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </main>

      <footer className="relative z-10 py-5 text-center text-xs text-white/80">
        {studio.name || "Dejavu Makeup by Vinni"}
      </footer>
    </div>
  );
}
